import React from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Filter } from 'lucide-react';

/**
 * Generic multi-select dropdown with chip summary.
 * Used for All Status / All Service Head / All Job Type / All Supervisor / All Technician filters.
 * Uses React Portal so the panel always floats above siblings (no stacking-context traps).
 *
 * Action header (mirrors the Lead module "All Leads" stages filter):
 *   [Select All]   [Default]   [Clear]
 *
 * Pass `defaults` (array) to enable the Default action; the button is hidden when no defaults are provided.
 * `onApplyDefault` (optional) — called instead of `onChange(defaults)` when Default is clicked
 * (lets the caller intersect with master list, etc.).
 */
export function MultiSelectFilter({
  label, options, selected, onChange,
  testId, accent = 'sky',
  defaults, onApplyDefault,
}) {
  const [open, setOpen] = React.useState(false);
  const buttonRef = React.useRef(null);
  const panelRef = React.useRef(null);
  const [coords, setCoords] = React.useState({ top: 0, left: 0, width: 256 });

  // Compute panel position whenever it opens or window scrolls
  const reposition = React.useCallback(() => {
    if (!buttonRef.current) return;
    const r = buttonRef.current.getBoundingClientRect();
    setCoords({
      top: r.bottom + 4,
      left: r.left,
      width: Math.max(r.width, 280),
    });
  }, []);

  React.useEffect(() => {
    if (!open) return;
    reposition();
    const onClick = (e) => {
      if (
        buttonRef.current && !buttonRef.current.contains(e.target) &&
        panelRef.current && !panelRef.current.contains(e.target)
      ) setOpen(false);
    };
    const onScroll = () => reposition();
    document.addEventListener('mousedown', onClick);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', onClick);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open, reposition]);

  const toggle = (v) => {
    const set = new Set(selected);
    if (set.has(v)) set.delete(v); else set.add(v);
    onChange([...set]);
  };

  const summary = selected.length === 0
    ? `All ${label}`
    : selected.length === 1 ? selected[0]
    : `${selected.length} ${label.toLowerCase()}`;

  const accentBorder = accent === 'rose' ? 'hover:border-rose-300 focus:border-rose-400'
    : accent === 'amber' ? 'hover:border-amber-300 focus:border-amber-400'
    : 'hover:border-sky-300 focus:border-sky-400';

  const showDefault = Array.isArray(defaults) && defaults.length > 0;

  const handleSelectAll = () => onChange([...options]);
  const handleDefault = () => {
    if (onApplyDefault) onApplyDefault();
    else onChange([...defaults]);
  };
  const handleClear = () => onChange([]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={testId}
        className={`px-3 py-2 rounded-xl bg-white border border-slate-200 text-[13px] text-slate-700 inline-flex items-center gap-1.5 ${accentBorder} ${selected.length ? 'border-sky-300 bg-sky-50' : ''}`}
      >
        <Filter size={12} />
        <span className="max-w-[150px] truncate">{summary}</span>
        <ChevronDown size={12} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && createPortal(
        <div
          ref={panelRef}
          style={{ position: 'fixed', top: coords.top, left: coords.left, width: coords.width, zIndex: 9999 }}
          className="max-h-72 overflow-y-auto rounded-xl bg-white border border-slate-200 shadow-2xl p-2"
        >
          {/* Action header */}
          <div className="flex items-center justify-between px-2 py-1 mb-1 sticky top-0 bg-white border-b border-slate-100 z-10">
            <span className="text-[10.5px] uppercase tracking-wider text-slate-400 font-medium">{label}</span>
            <div className="flex items-center gap-2.5 text-[11px] font-medium">
              <button
                onClick={handleSelectAll}
                data-testid={`${testId}-select-all`}
                className="text-blue-600 hover:underline"
              >
                Select All
              </button>
              {showDefault && (
                <button
                  onClick={handleDefault}
                  data-testid={`${testId}-default`}
                  className="text-emerald-600 hover:underline"
                >
                  Default
                </button>
              )}
              <button
                onClick={handleClear}
                data-testid={`${testId}-clear`}
                className="text-rose-600 hover:underline"
              >
                Clear
              </button>
            </div>
          </div>
          {options.length === 0 ? (
            <p className="text-[12px] text-slate-400 px-2 py-2">No options available</p>
          ) : options.map((opt) => (
            <label key={opt} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer">
              <input
                type="checkbox"
                checked={selected.includes(opt)}
                onChange={() => toggle(opt)}
                data-testid={`${testId}-opt-${opt}`}
                className="w-4 h-4 rounded border-slate-300 text-sky-600"
              />
              <span className="text-[12.5px] text-slate-700 truncate" title={opt}>{opt}</span>
            </label>
          ))}
        </div>,
        document.body
      )}
    </>
  );
}
